import { LinkedinTypes, ProductGroups } from 'enums';
import { FileUpload, FunnelContact, FunnelTypes, RecruiterLocationSearch, RegularConstraint, Search } from 'features/co-pilot/co-pilot.types';
import { Blacklist_New } from 'features/co-pilot/company-blacklists/company-blacklist.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import React from 'react';
import {
  BlacklistScopes,
  CampaignPipelineSteps,
  ContactFilter,
  ContactSearchMethods,
  ContactToAdd,
  LinkedinSearch,
  LinkedinSearchResult,
  QualificationPromptPlaceholders,
  Qualifications,
  SearchObjective,
  SearchToAdd,
  SingleSearch,
} from '../../campaign-builder.types';
import AddContacts from './add-contacts/add-contacts';
import ContactQualification from './ai-contact-qualification/contact-qualification';
import FinalContactsTable from './final-contacts/final-contacts-table';
import ManageBlacklists from './manage-blacklists/manage-blacklists';
import ContactsTable from './contact-overview/contacts-table';
import ManageSearches from './manage-searches/manage-searches';

interface Props {
  productGroup: ProductGroups;
  hasJudgingSettings: boolean;
  blacklistSyncLoading: boolean;
  funnelId: string;
  funnelType: FunnelTypes;
  activeCampaignPipelineStep: CampaignPipelineSteps;

  contactSearchMethod: ContactSearchMethods | '';

  linkedinProduct: LinkedinTypes | '';
  paiSearchLanguage: string[];
  includeOwnContacts: boolean;
  regularLocationConstraints: RegularConstraint;
  recruiterLocationConstraints: RecruiterLocationSearch;
  linkedinSearch: LinkedinSearch;

  selectedContactSearchObjective: SearchObjective | null;
  searchToAdd: SearchToAdd | null;
  linkedinAccounts: LinkedinAccount[];
  linkedinAccount: LinkedinAccount | undefined;
  separateLinkedinAccount: string;
  addSearchesLoading: boolean;

  contactsToAdd: ContactToAdd[];
  addContactsLoading: boolean;

  contactQualification: {
    loading: boolean;
    promptPlaceholders: QualificationPromptPlaceholders;
    selectedOptions: string[];
    rejudgeExistingContacts: boolean;
    prompt: string;
    hasOldKeys: boolean;
    isJudging: boolean;
  };

  searches: Search[];
  searchesLoading: boolean;

  activeBlacklistScope: BlacklistScopes;
  globalBlacklists: Blacklist_New[];
  localBlacklists: Blacklist_New[];
  globalBlacklistsLoading: boolean;
  localBlacklistsLoading: boolean;
  removeBlacklistItemsLoading: boolean;
  blacklists: string[];

  contactsPage: number;
  contactsSearchParam: string;
  contactsPerPage: number;
  totalContacts: number;
  contactFilter: ContactFilter;
  activeQualifications: Qualifications[];
  abortRequested: boolean;
  abortLoading: boolean;

  deleteMultipleContacts: boolean;
  contactsToDelete: FunnelContact[];

  aiJudgementProcessing: boolean;
  profileAnalysisProcessing: boolean;
  contacts: FunnelContact[];
  contactsLoading: boolean;

  finalContacts: FunnelContact[];
  finalContactsPage: number;
  finalContactsSearchParam: string;
  finalContactsPerPage: number;
  totalFinalContacts: number;
  finalContactsLoading: boolean;
  exportFinalContactsLoading: boolean;

  handleContactSearchMethodChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleMoveToNewSearch(): void;

  handleLinkedinProductChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handlePaiSearchLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleIncludeOwnContactsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleLocationFieldChange(event: React.ChangeEvent<HTMLInputElement>): void;

  searchKeyUp(): void;

  searchKeyDown(): void;

  handleMultiSelectLocationsChange(value: string): () => void;

  handleMultiSelectLocationsRemove(value: string): () => void;

  handleSelectRecruiterLocationConstraint(searchType: 'geo' | 'zip'): (value: string) => () => void;

  handleRemoveRecruiterLocationConstraint(searchType: 'geo' | 'zip', urn: string): () => void;

  handleRecruiterLocationConstraintQueryChange(searchType: 'geo' | 'zip'): (event: React.ChangeEvent<HTMLInputElement>) => void;

  searchRecruiterLocationKeyUp(searchType: 'geo' | 'zip'): () => void;

  searchRecruiterLocationKeyDown(): void;

  handleRecruiterLocationAreaChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleResetLinkedinSearch(): void;

  handleResetLocationSettings(): void;

  handleStartLinkedinSearch(): void;

  handleOpenRefineLinkedinSearchDialog(linkedinSearch: LinkedinSearchResult, idx: number): () => void;

  handleAiQualifierDetailsChange(type: keyof QualificationPromptPlaceholders): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleAiQualifierPromptChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAiQualifierSelectedOptionChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleRejudgeExistingContactsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleIsJudgingChange(event: React.ChangeEvent<HTMLInputElement>): void;

  updateQualifyingContactsRecruiting(): void;

  updateQualifyingContactsLead(): void;

  handleOpenCreateNewSearchDialog(): void;

  onOpenFeedbackDialog?(): void;

  handleSelectContactSearchObjective(newObjective: SearchObjective): () => void;

  handleOwnSearchFieldChange(fieldType: keyof SingleSearch): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handlePeopleTypeChange?: (type: 'includeReactions' | 'includeComments' | 'includeAuthor') => (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleProfileDegreeChange?: (value: 1 | 2 | 3) => (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleSeparateLinkedinAccountChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAddOwnLinkedinSearch(): void;

  handleAddOwnLinkedinSearchWithSkipQualification(): void;

  handleJobTitlesChange?: (titles: string[]) => void;

  onFileDrop(files: any): void;

  handleFileChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleDeleteContacts(): void;

  handleAddContactsFromFile(): void;

  handleOpenRenameContactSearchDialog(search: Search): () => void;

  handleOpenDeleteContactsSearchDialog(search: Search): () => void;

  handleOpenDeleteContactsFileDialog(file: FileUpload): () => void;

  handleOpenAddSingleContactDialog(): void;

  handleAddMultipleContacts(): void;

  handleOpenDeleteContactPermanentlyDialog(contact: FunnelContact): () => void;

  handleOpenChangeAiJudgementDialog(contact: FunnelContact): () => void;

  handleOpenLoadContactsNumberDialog(search: Search): () => void;

  handleOpenEditSearchLinkedinAccountDialog(search: Search): () => void;

  handleBlacklistScopeChange(newScope: BlacklistScopes): () => void;

  handleSelectedBlacklistsChange(blacklist: Blacklist_New): () => void;

  handleOpenAddLocalBlacklistDialog(): void;

  handleOpenEditBlacklistItemsDialog(blacklist: Blacklist_New): () => void;

  handleOpenEditBlacklistNameDialog(blacklist: Blacklist_New): () => void;

  handleOpenDeleteBlacklistDialog(blacklist: Blacklist_New): () => void;

  handleRemoveSingleBlacklistItem(blacklist: Blacklist_New): (identifier: string) => () => void;

  handleSingleContactIsActiveChange(contact: FunnelContact): () => void;

  handleMultiContactIsActiveChange(): void;

  handleChangeContactsPage(event: any, value: number): void;

  handleContactsSearchChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClearContactsSearchField(): void;

  handleOpenContactDrawer(linkedInURL: string | undefined, profileId: string | undefined, campaignId: string | undefined): () => void;

  handleOpenEnrichContactsDialog(): void;

  handleChangeFinalContactsPage(event: any, value: number): void;

  handleFinalContactsSearchChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClearFinalContactsSearchField(): void;

  exportCampaignContacts(): void;

  handleRemoveContactFromBlacklist(linkedInURL: string): () => void;

  handleRemoveContactFromSuspended(linkedInURL: string): () => void;

  handleOpenUpdateSexDialog(contact: FunnelContact): () => void;

  handleRecoverContactFromDeleted(linkedInURL: string): () => void;

  handleContactFilterChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleActiveQualificationsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAbortProcessing(): void;

  toggleDeleteMultipleContacts(): void;

  handleMultipleContactsToDeleteChange(event: React.MouseEvent<HTMLInputElement>): void;

  handleContactsToDeleteChange(contact: FunnelContact): (event: React.MouseEvent<HTMLInputElement>) => void;

  handleOpenDeleteMultipleContactsPermanentlyDialog(): void;
}

const CampaignPipeline: React.FC<Props> = ({
  productGroup,
  hasJudgingSettings,
  blacklistSyncLoading,
  funnelId,
  funnelType,
  activeCampaignPipelineStep,
  contactSearchMethod,
  linkedinProduct,
  paiSearchLanguage,
  includeOwnContacts,
  regularLocationConstraints,
  recruiterLocationConstraints,
  linkedinSearch,
  selectedContactSearchObjective,
  searchToAdd,
  linkedinAccounts,
  linkedinAccount,
  separateLinkedinAccount,
  addSearchesLoading,
  contactsToAdd,
  addContactsLoading,
  searches,
  searchesLoading,
  activeBlacklistScope,
  globalBlacklists,
  localBlacklists,
  globalBlacklistsLoading,
  localBlacklistsLoading,
  removeBlacklistItemsLoading,
  blacklists,
  contactsPage,
  contactsSearchParam,
  contactsPerPage,
  totalContacts,
  contactFilter,
  activeQualifications,
  abortRequested,
  abortLoading,
  deleteMultipleContacts,
  contactsToDelete,
  aiJudgementProcessing,
  profileAnalysisProcessing,
  contacts,
  contactsLoading,
  finalContacts,
  finalContactsPage,
  finalContactsSearchParam,
  finalContactsPerPage,
  totalFinalContacts,
  finalContactsLoading,
  exportFinalContactsLoading,
  handleContactSearchMethodChange,
  handleMoveToNewSearch,
  handleLinkedinProductChange,
  handlePaiSearchLanguageChange,
  handleIncludeOwnContactsChange,
  handleLocationFieldChange,
  searchKeyUp,
  searchKeyDown,
  handleMultiSelectLocationsChange,
  handleMultiSelectLocationsRemove,
  handleSelectRecruiterLocationConstraint,
  handleRemoveRecruiterLocationConstraint,
  handleRecruiterLocationConstraintQueryChange,
  searchRecruiterLocationKeyUp,
  searchRecruiterLocationKeyDown,
  handleRecruiterLocationAreaChange,
  handleResetLinkedinSearch,
  handleResetLocationSettings,
  handleStartLinkedinSearch,
  handleOpenRefineLinkedinSearchDialog,
  handleOpenCreateNewSearchDialog,
  onOpenFeedbackDialog,
  handleSelectContactSearchObjective,
  handleOwnSearchFieldChange,
  handlePeopleTypeChange,
  handleProfileDegreeChange,
  handleSeparateLinkedinAccountChange,
  handleAddOwnLinkedinSearch,
  handleAddOwnLinkedinSearchWithSkipQualification,
  handleJobTitlesChange,
  onFileDrop,
  handleFileChange,
  handleDeleteContacts,
  handleAddContactsFromFile,
  handleOpenRenameContactSearchDialog,
  handleOpenDeleteContactsSearchDialog,
  handleOpenDeleteContactsFileDialog,
  handleOpenAddSingleContactDialog,
  handleAddMultipleContacts,
  handleOpenDeleteContactPermanentlyDialog,
  handleOpenChangeAiJudgementDialog,
  handleOpenLoadContactsNumberDialog,
  handleOpenEditSearchLinkedinAccountDialog,
  handleBlacklistScopeChange,
  handleSelectedBlacklistsChange,
  handleOpenAddLocalBlacklistDialog,
  handleOpenEditBlacklistItemsDialog,
  handleOpenEditBlacklistNameDialog,
  handleOpenDeleteBlacklistDialog,
  handleRemoveSingleBlacklistItem,
  handleSingleContactIsActiveChange,
  handleMultiContactIsActiveChange,
  handleChangeContactsPage,
  handleContactsSearchChange,
  handleClearContactsSearchField,
  handleOpenContactDrawer,
  handleOpenEnrichContactsDialog,
  handleChangeFinalContactsPage,
  handleFinalContactsSearchChange,
  handleClearFinalContactsSearchField,
  exportCampaignContacts,
  contactQualification,
  handleAiQualifierDetailsChange,
  handleAiQualifierPromptChange,
  handleAiQualifierSelectedOptionChange,
  handleRejudgeExistingContactsChange,
  handleIsJudgingChange,
  updateQualifyingContactsRecruiting,
  updateQualifyingContactsLead,
  handleRemoveContactFromBlacklist,
  handleRemoveContactFromSuspended,
  handleOpenUpdateSexDialog,
  handleRecoverContactFromDeleted,
  handleContactFilterChange,
  handleActiveQualificationsChange,
  handleAbortProcessing,
  toggleDeleteMultipleContacts,
  handleMultipleContactsToDeleteChange,
  handleContactsToDeleteChange,
  handleOpenDeleteMultipleContactsPermanentlyDialog,
}) => {
  const getCampaignPipelineContent = () => {
    switch (activeCampaignPipelineStep) {
      case CampaignPipelineSteps.ADD_CONTACTS:
        return (
          <AddContacts
            productGroup={productGroup}
            targetFunnelId={funnelId}
            targetFunnelType={funnelType}
            contactSearchMethod={contactSearchMethod}
            linkedinProduct={linkedinProduct}
            paiSearchLanguage={paiSearchLanguage}
            includeOwnContacts={includeOwnContacts}
            regularLocationConstraints={regularLocationConstraints}
            recruiterLocationConstraints={recruiterLocationConstraints}
            linkedinSearch={linkedinSearch}
            selectedContactSearchObjective={selectedContactSearchObjective}
            searchToAdd={searchToAdd}
            linkedinAccounts={linkedinAccounts}
            linkedinAccount={linkedinAccount}
            separateLinkedinAccount={separateLinkedinAccount}
            addSearchesLoading={addSearchesLoading}
            contactsToAdd={contactsToAdd}
            addContactsLoading={addContactsLoading}
            handleContactSearchMethodChange={handleContactSearchMethodChange}
            handleLinkedinProductChange={handleLinkedinProductChange}
            handlePaiSearchLanguageChange={handlePaiSearchLanguageChange}
            handleIncludeOwnContactsChange={handleIncludeOwnContactsChange}
            handleLocationFieldChange={handleLocationFieldChange}
            searchKeyUp={searchKeyUp}
            searchKeyDown={searchKeyDown}
            handleMultiSelectLocationsChange={handleMultiSelectLocationsChange}
            handleMultiSelectLocationsRemove={handleMultiSelectLocationsRemove}
            handleSelectRecruiterLocationConstraint={handleSelectRecruiterLocationConstraint}
            handleRemoveRecruiterLocationConstraint={handleRemoveRecruiterLocationConstraint}
            handleRecruiterLocationConstraintQueryChange={handleRecruiterLocationConstraintQueryChange}
            searchRecruiterLocationKeyUp={searchRecruiterLocationKeyUp}
            searchRecruiterLocationKeyDown={searchRecruiterLocationKeyDown}
            handleRecruiterLocationAreaChange={handleRecruiterLocationAreaChange}
            handleResetLinkedinSearch={handleResetLinkedinSearch}
            handleResetLocationSettings={handleResetLocationSettings}
            handleStartLinkedinSearch={handleStartLinkedinSearch}
            handleOpenRefineLinkedinSearchDialog={handleOpenRefineLinkedinSearchDialog}
            handleOpenCreateNewSearchDialog={handleOpenCreateNewSearchDialog}
            onOpenFeedbackDialog={onOpenFeedbackDialog}
            handleSelectContactSearchObjective={handleSelectContactSearchObjective}
            handleOwnSearchFieldChange={handleOwnSearchFieldChange}
            handlePeopleTypeChange={handlePeopleTypeChange}
            handleProfileDegreeChange={handleProfileDegreeChange}
            handleSeparateLinkedinAccountChange={handleSeparateLinkedinAccountChange}
            handleAddOwnLinkedinSearch={handleAddOwnLinkedinSearch}
            handleAddOwnLinkedinSearchWithSkipQualification={handleAddOwnLinkedinSearchWithSkipQualification}
            handleJobTitlesChange={handleJobTitlesChange}
            onFileDrop={onFileDrop}
            handleFileChange={handleFileChange}
            handleDeleteContacts={handleDeleteContacts}
            handleAddContactsFromFile={handleAddContactsFromFile}
          />
        );
      case CampaignPipelineSteps.AI_CONTACT_QUALIFICATION:
        return (
          <ContactQualification
            contactQualification={contactQualification}
            productGroup={productGroup}
            handleAiQualifierDetailsChange={handleAiQualifierDetailsChange}
            handleAiQualifierPromptChange={handleAiQualifierPromptChange}
            handleAiQualifierSelectedOptionChange={handleAiQualifierSelectedOptionChange}
            handleRejudgeExistingContactsChange={handleRejudgeExistingContactsChange}
            handleIsJudgingChange={handleIsJudgingChange}
            updateQualifyingContactsRecruiting={updateQualifyingContactsRecruiting}
            updateQualifyingContactsLead={updateQualifyingContactsLead}
          />
        );
      case CampaignPipelineSteps.MANAGE_SEARCHES:
        return (
          <ManageSearches
            searches={searches}
            searchesLoading={searchesLoading}
            linkedinAccounts={linkedinAccounts}
            handleOpenRenameContactSearchDialog={handleOpenRenameContactSearchDialog}
            handleOpenDeleteContactsSearchDialog={handleOpenDeleteContactsSearchDialog}
            handleOpenDeleteContactsFileDialog={handleOpenDeleteContactsFileDialog}
            handleOpenLoadContactsNumberDialog={handleOpenLoadContactsNumberDialog}
            handleOpenEditSearchLinkedinAccountDialog={handleOpenEditSearchLinkedinAccountDialog}
            handleMoveToNewSearch={handleMoveToNewSearch}
          />
        );
      case CampaignPipelineSteps.MANAGE_BLACKLISTS:
        return (
          <ManageBlacklists
            blacklistSyncLoading={blacklistSyncLoading}
            activeBlacklistScope={activeBlacklistScope}
            globalBlacklists={globalBlacklists}
            localBlacklists={localBlacklists}
            globalBlacklistsLoading={globalBlacklistsLoading}
            localBlacklistsLoading={localBlacklistsLoading}
            removeBlacklistItemsLoading={removeBlacklistItemsLoading}
            blacklists={blacklists}
            handleBlacklistScopeChange={handleBlacklistScopeChange}
            handleSelectedBlacklistsChange={handleSelectedBlacklistsChange}
            handleOpenAddLocalBlacklistDialog={handleOpenAddLocalBlacklistDialog}
            handleOpenEditBlacklistItemsDialog={handleOpenEditBlacklistItemsDialog}
            handleOpenEditBlacklistNameDialog={handleOpenEditBlacklistNameDialog}
            handleOpenDeleteBlacklistDialog={handleOpenDeleteBlacklistDialog}
            handleRemoveSingleBlacklistItem={handleRemoveSingleBlacklistItem}
          />
        );
      case CampaignPipelineSteps.CONTACT_OVERVIEW:
        return (
          <ContactsTable
            funnelId={funnelId}
            contacts={contacts}
            searches={searches}
            productGroup={productGroup}
            hasJudgingSettings={hasJudgingSettings}
            contactsPage={contactsPage}
            contactsSearchParam={contactsSearchParam}
            contactsPerPage={contactsPerPage}
            totalContacts={totalContacts}
            contactsLoading={contactsLoading}
            aiJudgementProcessing={aiJudgementProcessing}
            profileAnalysisProcessing={profileAnalysisProcessing}
            contactFilter={contactFilter}
            activeQualifications={activeQualifications}
            abortRequested={abortRequested}
            abortLoading={abortLoading}
            deleteMultipleContacts={deleteMultipleContacts}
            contactsToDelete={contactsToDelete}
            handleAbortProcessing={handleAbortProcessing}
            handleContactFilterChange={handleContactFilterChange}
            handleActiveQualificationsChange={handleActiveQualificationsChange}
            handleSingleContactIsActiveChange={handleSingleContactIsActiveChange}
            handleMultiContactIsActiveChange={handleMultiContactIsActiveChange}
            handleChangeContactsPage={handleChangeContactsPage}
            handleContactsSearchChange={handleContactsSearchChange}
            handleClearContactsSearchField={handleClearContactsSearchField}
            handleOpenContactDrawer={handleOpenContactDrawer}
            handleOpenAddSingleContactDialog={handleOpenAddSingleContactDialog}
            handleAddMultipleContacts={handleAddMultipleContacts}
            handleOpenDeleteContactPermanentlyDialog={handleOpenDeleteContactPermanentlyDialog}
            handleOpenChangeAiJudgementDialog={handleOpenChangeAiJudgementDialog}
            handleRemoveContactFromBlacklist={handleRemoveContactFromBlacklist}
            handleRemoveContactFromSuspended={handleRemoveContactFromSuspended}
            handleOpenUpdateSexDialog={handleOpenUpdateSexDialog}
            handleRecoverContactFromDeleted={handleRecoverContactFromDeleted}
            toggleDeleteMultipleContacts={toggleDeleteMultipleContacts}
            handleMultipleContactsToDeleteChange={handleMultipleContactsToDeleteChange}
            handleContactsToDeleteChange={handleContactsToDeleteChange}
            handleOpenDeleteMultipleContactsPermanentlyDialog={handleOpenDeleteMultipleContactsPermanentlyDialog}
          />
        );
      case CampaignPipelineSteps.FINAL_CONTACT_LIST:
        return (
          <FinalContactsTable
            funnelId={funnelId}
            finalContacts={finalContacts}
            finalContactsPage={finalContactsPage}
            finalContactsSearchParam={finalContactsSearchParam}
            finalContactsPerPage={finalContactsPerPage}
            totalFinalContacts={totalFinalContacts}
            finalContactsLoading={finalContactsLoading}
            exportFinalContactsLoading={exportFinalContactsLoading}
            productGroup={productGroup}
            aiJudgementProcessing={aiJudgementProcessing}
            profileAnalysisProcessing={profileAnalysisProcessing}
            handleChangeFinalContactsPage={handleChangeFinalContactsPage}
            handleFinalContactsSearchChange={handleFinalContactsSearchChange}
            handleClearFinalContactsSearchField={handleClearFinalContactsSearchField}
            exportCampaignContacts={exportCampaignContacts}
            handleOpenEnrichContactsDialog={handleOpenEnrichContactsDialog}
            handleOpenContactDrawer={handleOpenContactDrawer}
          />
        );
      default:
        return <></>;
    }
  };

  return <>{getCampaignPipelineContent()}</>;
};

export default CampaignPipeline;
